import { useMemo, useState } from 'react';
import { SCOPE_WILDCARD } from '../../lib/constants';
import type { FacilityRecord } from '../../types/registry';

interface AssignFacilityDialogProps {
  facilities: FacilityRecord[];
  assignedFacilityIds: string[];
  onAdd: (facilityId: string, areaIds: string[]) => void;
  onCancel: () => void;
}

export function AssignFacilityDialog({
  facilities,
  assignedFacilityIds,
  onAdd,
  onCancel,
}: AssignFacilityDialogProps): JSX.Element {
  const available = useMemo(
    () => facilities.filter((facility) => !assignedFacilityIds.includes(facility.facility_id)),
    [facilities, assignedFacilityIds],
  );

  const [facilityId, setFacilityId] = useState(
    available.find((facility) => (facility.area_ids ?? []).length > 0)?.facility_id ?? '',
  );
  const [allAreas, setAllAreas] = useState(true);
  const [areaIds, setAreaIds] = useState<string[]>([]);

  const selectedFacility = facilities.find((facility) => facility.facility_id === facilityId);
  const facilityAreas = selectedFacility?.area_ids ?? [];
  const selectedHasAreas = facilityAreas.length > 0;
  const hasAssignableFacility = available.some((facility) => (facility.area_ids ?? []).length > 0);

  function selectFacility(nextId: string) {
    setFacilityId(nextId);
    setAllAreas(true);
    setAreaIds([]);
  }

  function toggleArea(areaId: string, checked: boolean) {
    setAreaIds((current) => {
      const existing = new Set(current);
      if (checked) existing.add(areaId);
      else existing.delete(areaId);
      return Array.from(existing);
    });
  }

  function handleAdd() {
    if (!facilityId || !selectedHasAreas) return;
    const resolved = allAreas || !areaIds.length ? [SCOPE_WILDCARD] : areaIds;
    onAdd(facilityId, resolved);
  }

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal-card" onClick={(event) => event.stopPropagation()}>
        <h2>Add facility</h2>
        <p className="modal-card__copy">Assign a facility and choose which areas the user can access.</p>

        {!available.length ? (
          <p className="hint-text">All facilities are already assigned.</p>
        ) : !hasAssignableFacility ? (
          <p className="hint-text">No facilities with areas available to assign. Add areas to a facility first.</p>
        ) : (
          <div className="invite-form">
            <label className="form-field">
              <span>Facility</span>
              <select value={facilityId} onChange={(event) => selectFacility(event.target.value)}>
                {available.map((facility) => {
                  const empty = (facility.area_ids ?? []).length === 0;
                  return (
                    <option key={facility.facility_id} value={facility.facility_id} disabled={empty}>
                      {facility.name}
                      {empty ? ' (empty - no areas)' : ''}
                    </option>
                  );
                })}
              </select>
            </label>

            {!selectedHasAreas ? (
              <p className="hint-text">This is an empty facility - add areas to it before assigning.</p>
            ) : (
              <>
                <label>
                  <input
                    type="checkbox"
                    checked={allAreas}
                    onChange={(event) => {
                      setAllAreas(event.target.checked);
                      if (event.target.checked) setAreaIds([]);
                    }}
                  />
                  All areas
                </label>

                {!allAreas ? (
                  <div className="area-list">
                    {facilityAreas.map((area) => (
                      <label key={area.area_id}>
                        <input
                          type="checkbox"
                          checked={areaIds.includes(area.area_id)}
                          onChange={(event) => toggleArea(area.area_id, event.target.checked)}
                        />
                        {area.name}
                      </label>
                    ))}
                  </div>
                ) : null}
              </>
            )}
          </div>
        )}

        <div className="modal-card__actions">
          <button
            className="primary-button"
            type="button"
            disabled={!facilityId || !available.length || !selectedHasAreas}
            onClick={handleAdd}
          >
            Add facility
          </button>
          <button className="ghost-button" type="button" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
